import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CategoriesService, CategoryStreams } from '../../../../services/accounting/categories.service';
import { ChartOfAccountsService, ChartOfAccountsStreams, COAAccount } from '../../../../services/accounting/chart-of-accounts.service';
import { BehaviorSubject, combineLatest, forkJoin, Observable } from 'rxjs';
import { Category, CATEGORY_ACCOUNT_TYPE, CategoryAccountType } from '../../../../models/entities/accounting/category';
import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { ASSET_ACCOUNT_TYPE, FixedAsset } from '../../../../models/entities/accounting/fixed-asset';
import { Liability, LIABILITY_ACCOUNT_TYPE } from '../../../../models/entities/accounting/liability';
import { Equity, EQUITY_ACCOUNT_TYPE } from '../../../../models/entities/accounting/equity';
import { map } from 'rxjs/operators';
import { SharedModule } from '../../../shared/shared.module';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { ButtonModule } from '../../../shared/button/button.module';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MaterialSearchComponent } from '../../../shared/material-search/material-search.component';
import { ToggleSwitchComponent } from '../../../shared/toggle-switch/toggle-switch.component';
import { MatLegacyChipsModule } from '@angular/material/legacy-chips';
import { AsPipe } from '../../../../pipes/format/as.pipe';
import { LegfiDatePipe } from '../../../../pipes/format/legfi-date.pipe';
import { CategoryEditorDialogComponent, CategoryEditorDialogInput } from '../../modules/accounting/chart-of-accounts/category-editor-dialog/category-editor-dialog.component';

/**
 * Unified selector for Chart of Accounts (COA) entities across revenue/expense categories and balance sheet accounts.
 *
 * Handles hierarchical category structures where parent categories contain child accounts,
 * and manages account lifecycle states (active, retired, deleted) with distinct visibility rules.
 * The multi-select mode supports parent-child toggle operations for bulk selection workflows.
 */
@UntilDestroy()
@Component({
    selector: 'app-category-select',
    templateUrl: './category-select.component.html',
    styleUrls: ['./category-select.shared.scss'],
    standalone: true,
    imports: [
        SharedModule,
        MatSelectModule,
        MatProgressSpinnerModule,
        MatIconModule,
        ButtonModule,
        MatSlideToggleModule,
        MatCheckboxModule,
        MaterialSearchComponent,
        ToggleSwitchComponent,
        MatLegacyChipsModule,
        AsPipe,
        LegfiDatePipe,
    ],
})
export class CategorySelectComponent implements OnInit, OnChanges
{
    // provides type narrowing in template, do not remove
    Category!: Category;

    @Input() showDeletedByDefault: boolean = false;
    @Input() showAddCategory = false;
    @Input() allowUncategorized = false;
    @Input() uncategorizedLabel = 'Uncategorized';
    @Input() formGroup: UntypedFormGroup;
    @Input() controlName = 'category';
    @Input() placeholder = 'Select a category';
    @Input() organizationId: number = null;

    @Input() showRevenueCategories = true;
    @Input() showExpenseCategories = true;
    @Input() showAssets = true;
    @Input() showLiabilities = true;
    @Input() showEquities = true;
    @Input() multiple = false;
    @Input() sections = [
        'revenue',
        'expense',
        'assets',
        'liabilities',
        'equities',
    ];

    // Accounts that should be displayed as disabled options even if they don't match filter criteria
    // This is used for legacy transfers that have non-asset accounts
    @Input() legacyDisabledAccounts: (FixedAsset | Liability | Equity)[] = [];

    // adding here as we clean up mat-select styles
    @Input() newStyles = false;

    @Input() preselected: string = '';

    categoryStreams: CategoryStreams;
    chartOfAccountStreams: ChartOfAccountsStreams;
    isLoading = false;

    $revenueCategories: Observable<Category[]>;
    $expenseCategories: Observable<Category[]>;
    $fixedAssets: Observable<FixedAsset[]>;
    $liabilities: Observable<Liability[]>;
    $equities: Observable<Equity[]>;

    // Observable streams for legacy disabled accounts (grouped by type)
    $legacyDisabledAssets: Observable<FixedAsset[]>;
    $legacyDisabledLiabilities: Observable<Liability[]>;
    $legacyDisabledEquities: Observable<Equity[]>;

    $searchUpdated = new BehaviorSubject<string>('');
    $showRetiredAccounts: BehaviorSubject<boolean> = new BehaviorSubject(false);
    $showDeletedAccounts: BehaviorSubject<boolean>;

    private $legacyDisabledAccountsSubject = new BehaviorSubject<(FixedAsset | Liability | Equity)[]>([]);

    hasRetiredAccounts = false;
    hasDeletedAccounts = false;

    constructor(
            private _categoriesService: CategoriesService,
            private _chartOfAccountsService: ChartOfAccountsService,
            private _fb: UntypedFormBuilder,
            private _cd: ChangeDetectorRef,
            private _dialog: MatDialog,
    ) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.legacyDisabledAccounts && !changes.legacyDisabledAccounts.isFirstChange()) {
            this.$legacyDisabledAccountsSubject.next(this.legacyDisabledAccounts);
        }
    }

    ngOnInit() {
        // Always initialize with false to hide deleted accounts by default
        this.$showDeletedAccounts = new BehaviorSubject(false);

        if (!this.formGroup) {
            this.formGroup = this._fb.group({
                [this.controlName]: [[]],
            });
        }

        if (this.organizationId === null) {
            const jwt = LegFiJwtService.read();
            this.organizationId = jwt.orgId;
        }

        if (this.multiple && this.preselected !== '') {
            const selections = this.preselected.split(',')
                    .map(sel => {
                        const parts = sel.split(':');
                        const id = parseInt(parts[1], 10);
                        switch (parts[0]) {
                            case CATEGORY_ACCOUNT_TYPE:
                                return new Category({id: id});
                            case ASSET_ACCOUNT_TYPE:
                                return new FixedAsset({id: id});
                            case LIABILITY_ACCOUNT_TYPE:
                                return new Liability({id: id});
                            case EQUITY_ACCOUNT_TYPE:
                                return new Equity({id: id});
                        }
                    });
            this.formGroup.get(this.controlName).setValue(selections);
        }

        // Initialize legacy disabled accounts streams
        this.initializeLegacyDisabledAccounts();
        this.$legacyDisabledAccountsSubject.next(this.legacyDisabledAccounts);

        this.loadOptions();

        combineLatest([
            this.categoryStreams.$categories,
            this.chartOfAccountStreams.liabilities.$data,
            this.chartOfAccountStreams.equities.$data,
            this.chartOfAccountStreams.assets.$data,
        ]).pipe(untilDestroyed(this)).subscribe({
            next: ([categories, liabilities, equities, assets]) => {
                this.hasRetiredAccounts = categories.some(category => category.retiredOn !== null)
                        || liabilities.some(liability => liability.retiredOn !== null)
                        || equities.some(equity => equity.retiredOn !== null)
                        || assets.some(asset => asset.retiredOn !== null);

                this.hasDeletedAccounts = categories.some(category => category.deletedAt !== null)
                        || liabilities.some(liability => liability.deletedAt !== null)
                        || equities.some(equity => equity.deletedAt !== null)
                        || assets.some(asset => asset.deletedAt !== null);
            },
        });
    }

    comparator(o1: COAAccount, o2: COAAccount) {
        return o1 && o2 && o1.accountType === o2.accountType && o1.id === o2.id;
    }

    resetSelections() {
        this.formGroup.get(this.controlName).setValue([]);
    }

    /**
     * Toggles all child accounts when parent category is selected in multi-select mode.
     *
     * Implements bulk selection pattern for hierarchical account structures where
     * parent categories serve as grouping mechanisms rather than standalone accounts.
     */
    toggleOptionChildren(option: Category) {
        if (!(option instanceof Category) || !option.children) {
            return;
        }

        const value = (this.formGroup.get(this.controlName).value as Category[]);
        if (value.find((v) => v.id === option.children[0].id)) {
            // deselect
            for (const child of option.children) {
                const index = value.findIndex((v) => v.id === child.id);
                if (index > -1) {
                    value.splice(index, 1);
                }
            }
        } else {
            // select
            for (const child of option.children) {
                if (!value.find((v) => v.id === child.id)) {
                    value.push(child);
                }
            }
        }

        this.formGroup.get(this.controlName).setValue(value);
    }

    getValue() {
        return this.formGroup.get(this.controlName).value;
    }

    addCategory(which: CategoryAccountType) {
        const data: CategoryEditorDialogInput = {
            category: null,
            type: which,
            isNew: true,
            parentCategoryId: null,
        };
        this._dialog.open(CategoryEditorDialogComponent, {
            data: data,
            width: MatDialogSizes.SM,
        }).afterClosed().subscribe({
            next: () => this.formGroup.patchValue({category: null}),
        });
    }

    /**
     * Initializes observables for legacy disabled accounts (non-asset accounts from old transfers).
     * These accounts will be shown as disabled options even when the component is filtered to only show assets.
     */
    private initializeLegacyDisabledAccounts() {
        // Filter legacy disabled accounts by type
        this.$legacyDisabledAssets = this.$legacyDisabledAccountsSubject.pipe(
            map(accounts => accounts.filter(acc => acc.accountType === ASSET_ACCOUNT_TYPE) as FixedAsset[])
        );

        this.$legacyDisabledLiabilities = this.$legacyDisabledAccountsSubject.pipe(
            map(accounts => accounts.filter(acc => acc.accountType === LIABILITY_ACCOUNT_TYPE) as Liability[])
        );

        this.$legacyDisabledEquities = this.$legacyDisabledAccountsSubject.pipe(
            map(accounts => accounts.filter(acc => acc.accountType === EQUITY_ACCOUNT_TYPE) as Equity[])
        );
    }

    /**
     * Loads COA accounts with reactive filtering for retired/deleted state visibility.
     *
     * Retired accounts represent historically active accounts preserved for reporting continuity.
     * Deleted accounts are soft-deleted entries that may need restoration during audits.
     * Parent categories filter independently from their children to support partial selections.
     */
    private loadOptions() {
        const toAwait: Observable<COAAccount[]>[] = [];
        this.categoryStreams = this._categoriesService.getOrganizationStreams(this.organizationId);

        this.$revenueCategories = combineLatest([
            this.categoryStreams.$revenueCategories,
            this.$searchUpdated.asObservable(),
            this.$showRetiredAccounts.asObservable(),
            this.$showDeletedAccounts.asObservable(),
        ]).pipe(map(([categories, searchValue, showRetired, showDeleted]) => {
            return categories.filter(category => {
                if (category.retiredOn && !showRetired) {
                    return false;
                }
                if (category.deletedAt && !showDeleted) {
                    return false;
                }
                const parentMatch = category.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1;
                const childMatch = category.children.some(child => {
                    if (!showDeleted && child.deletedAt) { return false; }
                    if (!showRetired && child.retiredOn) { return false; }
                    return child.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1;
                });
                return parentMatch || childMatch;
            }).map(category => {
                let children = category.children;
                if (!showDeleted) {
                    children = children.filter(child => !child.deletedAt);
                }
                if (!showRetired) {
                    children = children.filter(child => !child.retiredOn);
                }
                if (searchValue) {
                    children = children.filter(child => child.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1);
                }

                return new Category({
                    ...category,
                    children: children,
                });
            });
        }));

        this.$expenseCategories = combineLatest([
            this.categoryStreams.$expenseCategories,
            this.$searchUpdated.asObservable(),
            this.$showRetiredAccounts.asObservable(),
            this.$showDeletedAccounts.asObservable(),
        ]).pipe(map(([categories, searchValue, showRetired, showDeleted]) => {
            return categories.filter(category => {
                if (category.retiredOn && !showRetired) {
                    return false;
                }
                if (category.deletedAt && !showDeleted) {
                    return false;
                }
                const parentMatch = category.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1;
                const childMatch = category.children.some(child => {
                    if (!showDeleted && child.deletedAt) { return false; }
                    if (!showRetired && child.retiredOn) { return false; }
                    return child.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1;
                });
                return parentMatch || childMatch;
            }).map(category => {
                let children = category.children;
                if (!showDeleted) {
                    children = children.filter(child => !child.deletedAt);
                }
                if (!showRetired) {
                    children = children.filter(child => !child.retiredOn);
                }
                if (searchValue) {
                    children = children.filter(child => child.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1);
                }

                return new Category({
                    ...category,
                    children: children,
                });
            });
        }));

        this.chartOfAccountStreams = this._chartOfAccountsService.getOrganizationStreams(this.organizationId);

        this.$fixedAssets = combineLatest([
            this.chartOfAccountStreams.assets.$data,
            this.$searchUpdated.asObservable(),
            this.$showRetiredAccounts.asObservable(),
            this.$showDeletedAccounts.asObservable(),
            this.$legacyDisabledAccountsSubject.asObservable(),
        ]).pipe(map(([assets, searchValue, showRetired, showDeleted, legacyDisabled]) => {
            return assets.filter(asset => {
                if (asset.retiredOn && !showRetired) {
                    return false;
                }
                if (asset.deletedAt && !showDeleted) {
                    return false;
                }
                // filter out assets in legacy disabled list
                if (legacyDisabled.some(legacy => legacy.accountType === ASSET_ACCOUNT_TYPE && legacy.id === asset.id)) {
                    return false;
                }
                return asset.label.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1;
            });
        }));

        this.$liabilities = combineLatest([
            this.chartOfAccountStreams.liabilities.$data,
            this.$searchUpdated.asObservable(),
            this.$showRetiredAccounts.asObservable(),
            this.$showDeletedAccounts.asObservable(),
            this.$legacyDisabledAccountsSubject.asObservable(),
        ]).pipe(map(([liabilities, searchValue, showRetired, showDeleted, legacyDisabled]) => {
            return liabilities.filter(liability => {
                if (liability.retiredOn && !showRetired) {
                    return false;
                }
                if (liability.deletedAt && !showDeleted) {
                    return false;
                }
                // filter out liabilities in legacy disabled list
                if (legacyDisabled.some(legacy => legacy.accountType === LIABILITY_ACCOUNT_TYPE && legacy.id === liability.id)) {
                    return false;
                }
                return liability.label.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1;
            });
        }));

        this.$equities = combineLatest([
            this.chartOfAccountStreams.equities.$data,
            this.$searchUpdated.asObservable(),
            this.$showRetiredAccounts.asObservable(),
            this.$showDeletedAccounts.asObservable(),
            this.$legacyDisabledAccountsSubject.asObservable(),
        ]).pipe(map(([equities, searchValue, showRetired, showDeleted, legacyDisabled]) => {
            return equities.filter(equity => {
                if (equity.retiredOn && !showRetired) {
                    return false;
                }
                if (equity.deletedAt && !showDeleted) {
                    return false;
                }
                // filter out equities in legacy disabled list
                if (legacyDisabled.some(legacy => legacy.accountType === EQUITY_ACCOUNT_TYPE && legacy.id === equity.id)) {
                    return false;
                }
                return equity.label.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1;
            });
        }));

        if (this.showRevenueCategories || this.showExpenseCategories) {
            if (!this.categoryStreams.$loaded.getValue()) {
                toAwait.push(this._categoriesService.getAllCategories(this.organizationId));
            }
        }

        if (this.showAssets || this.showLiabilities || this.showEquities) {
            if (this.showAssets) {
                if (!this.chartOfAccountStreams.assets.$loaded.getValue()) {
                    toAwait.push(this._chartOfAccountsService.getAssets(this.organizationId));
                }
            }

            if (this.showLiabilities) {
                if (!this.chartOfAccountStreams.liabilities.$loaded.getValue()) {
                    toAwait.push(this._chartOfAccountsService.getLiabilities(this.organizationId));
                }
            }

            if (this.showEquities) {
                if (!this.chartOfAccountStreams.equities.$loaded.getValue()) {
                    toAwait.push(this._chartOfAccountsService.getEquities(this.organizationId));
                }
            }
        }

        if (toAwait.length > 0) {
            this.isLoading = true;
            forkJoin(toAwait).pipe(untilDestroyed(this)).subscribe({
                next: () => this.isLoading = false,
            });
        } else {
            this.isLoading = false;
            this._cd.detectChanges();
        }
    }
}
